import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import axios from 'axios';
import {
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  BanknotesIcon,
  UserGroupIcon,
  ChevronDownIcon,
  EnvelopeIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';

export default function AdminSuiviAssurance() {
  const { user } = useAuth();
  const { canAccessUnafServices, canGenerateUnafExport, canSendUnafExport, canActivateUnafExport, canPayUnafExport } = usePermissions();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [exports, setExports] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exportingCampaign, setExportingCampaign] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showPendingDetails, setShowPendingDetails] = useState(false);
  const [openDownloadMenu, setOpenDownloadMenu] = useState(null);

  useEffect(() => {
    // Vérifier l'accès au module via permissions
    if (!user || !canAccessUnafServices()) {
      navigate('/');
      return;
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedYear, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, exportsRes, pendingRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/unaf-export/stats?annee=${selectedYear}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/unaf-export/list?annee=${selectedYear}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/unaf-export/pending?annee=${selectedYear}`, { headers }),
      ]);

      setStats(statsRes.data);
      setExports(exportsRes.data);
      setPendingPayments(pendingRes.data.payments || []);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier si tous les exports précédents ont leurs adhésions activées
  const canGenerateNewExport = () => {
    if (!canGenerateUnafExport()) return false;
    
    // Vérifier si tous les exports ont été activés
    const hasUnactivatedExports = exports.some(exp => !exp.dateActivation);
    return !hasUnactivatedExports;
  };

  const handleGenerateExport = async () => {
    if (!canGenerateUnafExport()) {
      setError('Vous n\'avez pas la permission de générer un export');
      return;
    }
    
    if (!canGenerateNewExport()) {
      setError('Vous devez activer les adhésions de tous les exports précédents avant de générer un nouvel export');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/unaf-export/generate`,
        { annee: selectedYear },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(`Export généré avec succès: ${response.data.nombrePaiements} paiements inclus`);
      fetchData();
    } catch (err) {
      console.error('Erreur génération:', err);
      setError(err.response?.data?.message || 'Erreur lors de la génération de l\'export');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportCampaignExcel = async () => {
    try {
      setExportingCampaign(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/unaf-export/campaign-excel?annee=${selectedYear}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      const contentDisposition = response.headers['content-disposition'];
      const fileNameMatch = contentDisposition?.match(/filename="(.+)"/);
      const fileName = fileNameMatch?.[1] || `UNAF_campagne_${selectedYear}.xlsx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur export campagne:', err);
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          setError(parsed.message || 'Erreur lors de l\'export Excel');
        } catch {
          setError('Erreur lors de l\'export Excel');
        }
      } else {
        setError(err.response?.data?.message || 'Erreur lors de l\'export Excel');
      }
    } finally {
      setExportingCampaign(false);
    }
  };

  const handleDownload = async (exportId, fileType = 'principal') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/unaf-export/download/${exportId}?fileType=${fileType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.open(response.data.url, '_blank');
      setOpenDownloadMenu(null); // Fermer le menu après téléchargement
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      setError('Erreur lors du téléchargement');
    }
  };

  const [sending, setSending] = useState(null);
  const [activating, setActivating] = useState(null);
  const [paying, setPaying] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(null);

  const handleSendExport = async (exportId) => {
    if (!canSendUnafExport()) return;

    try {
      setSending(exportId);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/unaf-export/${exportId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(response.data.message || 'Export envoyé avec succès');
      fetchData();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSending(null);
    }
  };

  const handleActivateExport = async (exportId) => {
    if (!canActivateUnafExport()) return;

    try {
      setActivating(exportId);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/unaf-export/${exportId}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(response.data.message || 'Adhésions activées avec succès');
      fetchData();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'activation');
    } finally {
      setActivating(null);
    }
  };

  const handlePayExport = async (exportId) => {
    try {
      setPaying(exportId);
      setError('');
      setShowPaymentModal(null);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/unaf-export/${exportId}/pay`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess(response.data.message || 'Paiement initié avec succès');
      fetchData();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Erreur lors du paiement');
    } finally {
      setPaying(null);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', {
    weekday: "long",
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute:'2-digit'
    });
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Suivi services UNAF</h1>
              <p className="text-slate-600 mt-1">
                Gestion des exports pour l'Union Nationale de l'Apiculture Française
              </p>
            </div>
            <HelpButton 
              title={helpContent.suiviAssurance.title}
              sections={helpContent.suiviAssurance.sections}
            />
            <div className="flex items-center gap-3">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
              <button
                onClick={handleExportCampaignExcel}
                disabled={exportingCampaign}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
                {exportingCampaign ? 'Export en cours...' : 'Exporter Excel'}
              </button>
              <button
                onClick={fetchData}
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <UserGroupIcon className="h-8 w-8 text-indigo-500" />
              <span className="text-2xl font-bold text-slate-900">{stats?.totalServices || 0}</span>
            </div>
            <p className="text-sm text-slate-600">Souscriptions payées</p>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <span className="text-2xl font-bold text-slate-900">{stats?.exportedServices || 0}</span>
            </div>
            <p className="text-sm text-slate-600">Déjà exportées</p>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <ClockIcon className="h-8 w-8 text-honey-500" />
              <span className="text-2xl font-bold text-slate-900">{stats?.totalPending || 0}</span>
            </div>
            <p className="text-sm text-slate-600">En attente d'export</p>
            <p className="text-xs text-slate-400 mt-1">
              ({stats?.pendingServices || 0} initiaux + {stats?.pendingModifications || 0} modifs)
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <CalendarDaysIcon className="h-8 w-8 text-blue-500" />
              <span className="text-sm font-medium text-slate-900">
                {stats?.nextExportDate ? formatDate(stats.nextExportDate) : 'Aucune'}
              </span>
            </div>
            <p className="text-sm text-slate-600">Prochain export prévu</p>
          </div>
        </div>

        {/* Actions */}
        {canGenerateUnafExport() && stats?.totalPending > 0 && (
          <div className="bg-honey-50 border border-honey-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-6 w-6 text-honey-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-honey-800">
                    {stats.totalPending} paiement(s) en attente d'export
                  </h3>
                  <p className="text-sm text-honey-700 mt-1">
                    Vous pouvez générer un export manuellement ou attendre la prochaine date automatique.
                  </p>
                </div>
              </div>
              <button
                onClick={handleGenerateExport}
                disabled={generating || !canGenerateNewExport()}
                className="flex items-center px-4 py-2 bg-honey-500 text-white rounded-lg hover:bg-honey-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={!canGenerateNewExport() ? 'Activez les adhésions de tous les exports précédents avant de générer un nouvel export' : ''}
              >
                {generating ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Générer maintenant
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Paiements en attente */}
        {pendingPayments.length > 0 && (
          <div className="bg-white rounded-xl shadow-soft mb-8">
            <button
              onClick={() => setShowPendingDetails(!showPendingDetails)}
              className="w-full flex items-center justify-between p-6 text-left"
            >
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-honey-500 mr-3" />
                <span className="font-semibold text-slate-900">
                  Paiements en attente d'export ({pendingPayments.length})
                </span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-slate-400 transition-transform ${showPendingDetails ? 'rotate-180' : ''}`}
              />
            </button>

            {showPendingDetails && (
              <div className="border-t border-slate-100 p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-500 border-b border-slate-100">
                        <th className="pb-3 font-medium">Adhérent</th>
                        <th className="pb-3 font-medium">Email</th>
                        <th className="pb-3 font-medium">Type</th>
                        <th className="pb-3 font-medium">Montant</th>
                        <th className="pb-3 font-medium">Date paiement</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {pendingPayments.map((payment, index) => (
                        <tr key={index} className="text-sm">
                          <td className="py-3">
                            {payment.user?.prenom} {payment.user?.nom}
                          </td>
                          <td className="py-3 text-slate-600">{payment.user?.email}</td>
                          <td className="py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                payment.type === 'initial'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {payment.type === 'initial' ? 'Souscription' : 'Modification'}
                            </span>
                          </td>
                          <td className="py-3 font-medium">{formatMontant(payment.montant)}</td>
                          <td className="py-3 text-slate-600">{formatDate(payment.datePaiement)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Calendrier des exports */}
        <div className="mt-8 bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center mb-6">
            <CalendarDaysIcon className="h-5 w-5 text-slate-500 mr-3" />
            <h2 className="font-semibold text-slate-900">Dates d'export programmées ({selectedYear})</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {stats?.exportDates?.map((date, index) => {
              const d = new Date(date);
              const isPast = d < new Date();
              const isNext = stats.nextExportDate && new Date(stats.nextExportDate).getTime() === d.getTime();
              const hasExport = exports.some(
                (exp) => new Date(exp.dateExport).toDateString() === d.toDateString()
              );

              return (
                <div
                  key={index}
                  className={`p-3 rounded-lg border text-center ${
                    isNext
                      ? 'border-honey-300 bg-honey-50'
                      : hasExport
                      ? 'border-green-300 bg-green-50'
                      : isPast
                      ? 'border-slate-200 bg-slate-50'
                      : 'border-slate-200'
                  }`}
                >
                  <p
                    className={`text-sm font-medium ${
                      isNext
                        ? 'text-honey-700'
                        : hasExport
                        ? 'text-green-700'
                        : isPast
                        ? 'text-slate-400'
                        : 'text-slate-700'
                    }`}
                  >
                    {d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {isNext ? 'Prochain' : hasExport ? 'Exporté' : isPast ? 'Passé' : 'À venir'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Liste des exports */}
        <div className="bg-white rounded-xl shadow-soft">
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-slate-500 mr-3" />
              <h2 className="font-semibold text-slate-900">Historique des exports</h2>
            </div>
          </div>

          {exports.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun export pour cette année</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {exports.map((exp) => (
                <div key={exp._id} className="p-6 flex items-center justify-between">
                  <div className="flex items-start">
                    <div
                      className={`p-2 rounded-lg mr-4 ${
                        exp.status === 'envoye' ? 'bg-green-100' : 'bg-slate-100'
                      }`}
                    >
                      <DocumentArrowDownIcon
                        className={`h-5 w-5 ${
                          exp.status === 'envoye' ? 'text-green-600' : 'text-slate-600'
                        }`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">
                          Export du {formatDate(exp.dateExport)}
                        </span>
                        {exp.isFirstExport && (
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                            Premier export
                          </span>
                        )}
                        <span
                          className={`px-2 py-0.5 text-xs rounded-full ${
                            exp.status === 'envoye' ? 'bg-green-100 text-green-700'
                            : exp.status === 'paye' ? 'bg-blue-100 text-blue-700'
                            : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {exp.status === 'envoye' ? 'Envoyé' 
                          : exp.status === 'paye' ? 'Payé' : 'Généré'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {exp.nombrePaiements} paiement(s) • {formatMontant(exp.montantTotal)}
                      </p>
                      {exp.notes && (
                        <p className="text-xs text-slate-400 mt-1">{exp.notes}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Bouton de téléchargement - simple ou menu déroulant selon les fichiers disponibles */}
                    {(() => {
                      const hasPrincipal = exp.fichierPrincipal && (exp.fichierPrincipal.s3Key || exp.fichierPrincipal.s3Url || exp.s3Key);
                      const hasComplement = exp.fichierComplement && (exp.fichierComplement.s3Key || exp.fichierComplement.s3Url);
                      const fileCount = (hasPrincipal ? 1 : 0) + (hasComplement ? 1 : 0);

                      // Si un seul fichier, bouton simple
                      if (fileCount === 1) {
                        const fileType = hasPrincipal ? 'principal' : 'complement';
                        return (
                          <button
                            onClick={() => handleDownload(exp._id, fileType)}
                            className="flex items-center px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                            Télécharger
                          </button>
                        );
                      }

                      // Si plusieurs fichiers, menu déroulant
                      if (fileCount > 1) {
                        return (
                          <div className="relative">
                            <button
                              onClick={() => setOpenDownloadMenu(openDownloadMenu === exp._id ? null : exp._id)}
                              className="flex items-center px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                            >
                              <DocumentArrowDownIcon className="h-4 w-4 mr-1.5" />
                              Télécharger
                              <ChevronDownIcon className="h-3 w-3 ml-1" />
                            </button>
                            
                            {openDownloadMenu === exp._id && (
                              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                                {hasPrincipal && (
                                  <button
                                    onClick={() => handleDownload(exp._id, 'principal')}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                                  >
                                    <DocumentArrowDownIcon className="h-4 w-4 mr-2 text-slate-500" />
                                    <div>
                                      <div className="font-medium">Fichier principal</div>
                                      <div className="text-xs text-slate-500">
                                        {exp.fichierPrincipal?.nombrePaiements || 0} paiement(s)
                                      </div>
                                    </div>
                                  </button>
                                )}
                                {hasComplement && (
                                  <button
                                    onClick={() => handleDownload(exp._id, 'complement')}
                                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center"
                                  >
                                    <DocumentArrowDownIcon className="h-4 w-4 mr-2 text-slate-500" />
                                    <div>
                                      <div className="font-medium">Fichier complément</div>
                                      <div className="text-xs text-slate-500">
                                        {exp.fichierComplement?.nombrePaiements || 0} paiement(s)
                                      </div>
                                    </div>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Aucun fichier disponible
                      return (
                        <span className="text-xs text-slate-400 italic">Aucun fichier</span>
                      );
                    })()}
                    
                    {canPayUnafExport() && exp.status === 'genere' && (
                      <button
                        onClick={() => setShowPaymentModal(exp)}
                        disabled={paying === exp._id}
                        className="flex items-center px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50"
                      >
                        {paying === exp._id ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                            Paiement...
                          </>
                        ) : (
                          <>
                            <BanknotesIcon className="h-4 w-4 mr-1.5" />
                            Envoyer le paiement
                          </>
                        )}
                      </button>
                    )}
                    {exp.paiement?.payoutId && (
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1.5 text-sm rounded-lg ${
                          exp.paiement.status === 'paid' 
                            ? 'bg-green-100 text-green-700' 
                            : exp.paiement.status === 'in_transit'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {exp.paiement.status === 'paid' ? '✓ Payé' : exp.paiement.status === 'in_transit' ? 'En transit' : 'En attente'}
                        </span>
                        {exp.paiement.preuveUrl && (
                          <button
                            onClick={() => window.open(exp.paiement.preuveUrl, '_blank')}
                            className="flex items-center px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                          >
                            <DocumentTextIcon className="h-4 w-4 mr-1.5" />
                            Preuve
                          </button>
                        )}
                      </div>
                    )}
                    {canSendUnafExport() && exp?.status !== "envoye" && exp.paiement?.preuveUrl && exp.paiement?.payoutId && (
                      <button
                        onClick={() => handleSendExport(exp._id)}
                        disabled={sending === exp._id}
                        className="flex items-center px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
                      >
                        {sending === exp._id ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                            Envoi...
                          </>
                        ) : (
                          <>
                            <EnvelopeIcon className="h-4 w-4 mr-1.5" />
                            Envoyer le mail
                          </>
                        )}
                      </button>
                    )}
                    {canActivateUnafExport() && exp.status === 'envoye' && !exp.dateActivation && (
                      <button
                        onClick={() => handleActivateExport(exp._id)}
                        disabled={activating === exp._id}
                        className="flex items-center px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors disabled:opacity-50"
                      >
                        {activating === exp._id ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                            Activation...
                          </>
                        ) : (
                          <>
                            <PlayIcon className="h-4 w-4 mr-1.5" />
                            Activer les adhésions
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modale de confirmation de paiement */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center mb-4">
                <BanknotesIcon className="h-6 w-6 text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold text-slate-900">Confirmer le paiement</h3>
              </div>
              
              <div className="mb-6">
                <p className="text-slate-600 mb-4">
                  Vous êtes sur le point d'initier un virement Stripe vers l'UNAF pour cet export.
                </p>
                
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Montant total :</span>
                    <span className="font-semibold text-slate-900">{formatMontant(showPaymentModal.montantTotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Nombre de paiements :</span>
                    <span className="font-semibold text-slate-900">{showPaymentModal.nombrePaiements}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Date d'export :</span>
                    <span className="font-semibold text-slate-900">
                      {new Date(showPaymentModal.dateExport).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note :</strong> Le virement sera effectué via Stripe Payouts. 
                    Une preuve de virement sera générée automatiquement.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(null)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handlePayExport(showPaymentModal._id)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Confirmer le paiement
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
